<template>
  <Query
    :query="query"
    :variables="variables"
    :debounce="500"
    @error="onError"
    fetchPolicy="cache-first"
    v-slot="{ result: { error, data }, isLoading }"
  >
    <div class="mt-12">
      <div class="hidden sm:block border-b mb-4">
        <nav class="flex space-x-8 align-center">
          <button @click.prevent="tab = 'roster'" class="whitespace-nowrap pb-2 px-1 border-b-2  font-medium text-sm leading-5 focus:outline-none -mb-px"
          :class="[
            tab == 'roster' ?
            'border-indigo-500 text-indigo-500' :
            'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 focus:text-gray-700 focus:border-gray-300'
            ]"
          >
            Current Roster ({{value.length}})
          </button>


          <button @click.prevent="tab = 'students'" class="whitespace-nowrap pb-2 px-1 border-b-2  font-medium text-sm leading-5 focus:outline-none -mb-px"
          :class="[
            tab == 'students' ?
            'border-indigo-500 text-indigo-500' :
            'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 focus:text-gray-700 focus:border-gray-300'
            ]"
          >
            Add Students
          </button>

        </nav>
      </div>
      <div v-if="tab == 'students'">

          <div class="relative">
            <div class="inline-block w-full rounded-md shadow-sm">
              <input id="account_number" class="form-input block w-full pr-10 sm:text-sm sm:leading-5" placeholder="Search by student name"
              v-model="search"
              ref="search"
              >
              <div class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                <font-awesome-icon v-if="isLoading" :icon="['far', 'spinner']" class="animate-spin"></font-awesome-icon>
                <font-awesome-icon v-else :icon="['far', 'search']" class="h-5 w-5 text-gray-400" />
              </div>
            </div>
          </div>

          <div v-if="!isLoading && data && data.students && data.students.length == 0 && search.length > 0">
            <div class="mt-6 rounded-md bg-cool-gray-100 p-4">
              <div class="flex">
                <div class="ml-3">
                  <h3 class="text-sm leading-5 font-medium text-cool-gray-800">
                    No Students Found
                  </h3>
                </div>
              </div>
            </div>
          </div>


          <div class="bg-white overflow-hidden sm:rounded-md" v-else>
            <ul>
              <li v-for="student in data.students" class="border-b last:border-none" :key="student.id">
                <div class="px-4 py-4 flex items-center sm:px-6">
                  <div class="min-w-0 flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <div class="text-sm leading-5 font-medium text-indigo-600 truncate">
                        <StudentName :student="student" v-slot="{ display }">{{ display }}</StudentName>
                      </div>
                      <div class="mt-2 flex">
                        <div class="flex items-center text-sm leading-5 text-gray-500">
                          <span>
                            <Get :object="student" get="gradeLevel.name" v-slot="{found}">{{found}}</Get>
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <button @click.prevent="didAddStudent(student)" class="ml-5 flex-shrink-0 h-8 w-8 border flex items-center justify-center rounded">
                    <font-awesome-icon :icon="['far', 'plus']"></font-awesome-icon>
                  </button>
                </div>
              </li>
            </ul>
          </div>
      </div>

      <div v-if="tab == 'roster'">
          <div v-if="value.length == 0">
            <div class="mt-6 rounded-md bg-cool-gray-100 p-4">
              <div class="flex">
                <div class="ml-3">
                  <h3 class="text-sm leading-5 font-medium text-cool-gray-800">
                    No Students Selected Yet
                  </h3>
                </div>
              </div>
            </div>
          </div>

          <div class="bg-white overflow-hidden sm:rounded-md">
            <ul>
              <li v-for="student in value" class="border-b last:border-none" :key="student.id">
                <div class="px-4 py-4 flex items-center sm:px-6">
                  <div class="min-w-0 flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <div class="text-sm leading-5 font-medium text-indigo-600 truncate">
                        <StudentName :student="student" v-slot="{ display }">{{ display }}</StudentName>
                      </div>
                      <div class="mt-2 flex">
                        <div class="flex items-center text-sm leading-5 text-gray-500">
                          <span>
                            <Get :object="student" get="gradeLevel.name" v-slot="{found}">{{found}}</Get>
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <button @click.prevent="didRemoveStudent(student)" class="rounded-md ml-5 flex-shrink-0 inline-flex -ml-px relative items-center px-4 py-2 border border-gray-300 bg-white text-sm leading-5 font-medium text-gray-700 hover:text-gray-500 focus:z-10 focus:outline-none focus:border-blue-300 focus:ring-blue active:bg-gray-100 active:text-gray-700 transition ease-in-out duration-150">
                    <font-awesome-icon :icon="['far', 'minus-circle']"></font-awesome-icon>
                  </button>
                </div>
              </li>
            </ul>
          </div>
      </div>
    </div>
  </Query>
</template>

<script>
import _union from 'lodash-es/union'
import _without from 'lodash-es/without'

import StudentName from '@/components/StudentName.vue'
import StudentsQuery from '@/queries/Students.gql'
import Get from '@/snippets/Get.vue'
import Query from '@/components/Query.vue'

export default {
  components: {
    Get,
    Query,
    StudentName,
  },

  props: {
    teamId: {
      type: String,
      required: true
    },
    value: {
      type: Array,
      required: true
    }
  },

  data: function () {
    return {
      query: StudentsQuery,
      search: '',
      tab: 'roster',
    }
  },

  computed: {
    variables() {
      return {
        teamId: this.teamId,
        search: this.search,
        limit: 5,
      }
    }
  },

  methods: {
    didAddStudent(student) {
      this.$emit('input', _union(this.value, [student]))
      this.$refs.search.select()
    },

    didRemoveStudent(student) {
      this.$emit('input', _without(this.value, student))
    },

    onError(e) {
      console.log('got here')
      Bus.$emit('flash-message', {text: e, type: 'error'})
    },
  }
}
</script>
